import { css } from '@emotion/react';

export const BUTTON_VARIANTS = {
  PRIMARY: 'primary',
  SECONDARY: 'secondary',
  TERTIARY: 'tertiary',
  DESTRUCTIVE_PRIMARY: 'destructivePrimary',
  DESTRUCTIVE_SECONDARY: 'destructiveSecondary',
  SUCCESS_PRIMARY: 'successPrimary',
  SUCCESS_SECONDARY: 'successSecondary',
  CONTRAST_PRIMARY: 'contrastPrimary',
  CONTRAST_SECONDARY: 'contrastSecondary',
  TEXT_PRIMARY: 'textPrimary',
  TEXT_SECONDARY: 'textSecondary',
  TEXT_DESTRUCTIVE: 'textDestructive',
};

const buttonPaddingCss = (paddingSize) => css`
  padding: ${paddingSize};
`;

const buttonBaseCss = (theme) => css`
  align-items: center;
  border-radius: ${theme.shape.borderRadius}px;
  display: flex;
  cursor: pointer;
  flex-grow: 0;
  font-family: ${theme.typography.fontFamily};
  font-size: ${theme.typography.fontSize}px;
  font-stretch: normal;
  font-style: normal;
  font-weight: 600;
  height: 40px;
  letter-spacing: 0.2px;
  line-height: 1.5;
  text-align: left;
  white-space: nowrap;
  &:hover {
    text-decoration: none;
  }
  &:focus {
    box-shadow: 0 0 0 2px ${theme.palette.common.white};
    outline: ${theme.palette.primary.dark} solid 2px;
    outline-offset: 4px;
    text-decoration: none;
  }
  &:disabled {
    pointer-events: none;
  }
`;

export const BUTTON_VARIANT_CSS = {
  [BUTTON_VARIANTS.PRIMARY]: (theme) => css`
    ${buttonBaseCss(theme)}
    ${buttonPaddingCss(theme.spacing(0, 6))}
    background-color: ${theme.palette.primary.main};
    border-color: transparent;
    color: ${theme.palette.common.white} !important;
    &:hover {
      background-color: ${theme.palette.primary.dark};
    }
    &:disabled {
      background-color: ${theme.palette.grey[5]};
      color: ${theme.palette.grey[60]};
    }
    & svg path {
      fill: ${theme.palette.common.white};
    }
    &:disabled svg path {
      fill: ${theme.palette.grey[60]};
    }
  `,
  [BUTTON_VARIANTS.SECONDARY]: (theme) => css`
    ${buttonBaseCss(theme)}
    ${buttonPaddingCss(theme.spacing(0, 6))}
    background-color: ${theme.palette.common.white};
    border: 1px solid ${theme.palette.primary[50]};
    color: ${theme.palette.primary[50]};
    &:hover {
      background-color: ${theme.palette.primary[5]};
    }
    &:disabled {
      background-color: ${theme.palette.common.white};
      border-color: ${theme.palette.grey[40]};
      color: ${theme.palette.grey[60]};
    }
    & svg path {
      fill: ${theme.palette.primary.main};
    }
    &:disabled svg path {
      fill: ${theme.palette.grey[60]};
    }
  `,
  [BUTTON_VARIANTS.TERTIARY]: (theme) => css`
    ${buttonBaseCss(theme)}
    ${buttonPaddingCss(theme.spacing(0, 6))}
    background-color: ${theme.palette.common.white};
    border: 1px solid ${theme.palette.grey[60]};
    color: ${theme.palette.grey[100]};
    &:hover,
    &:focus {
      border-color: ${theme.palette.grey[40]};
      border-width: 1px;
    }
    &:disabled {
      border-color: ${theme.palette.grey[40]};
      color: ${theme.palette.grey[60]};
    }
    & svg path {
      fill: ${theme.palette.grey[100]};
    }
    &:disabled svg path {
      fill: ${theme.palette.grey[60]};
    }
  `,
  [BUTTON_VARIANTS.DESTRUCTIVE_PRIMARY]: (theme) => css`
    ${buttonBaseCss(theme)}
    ${buttonPaddingCss(theme.spacing(0, 6))}
    background-color: ${theme.palette.alert.main};
    border-color: transparent;
    color: ${theme.palette.common.white};
    &:hover {
      background-color: ${theme.palette.alert[100]};
    }
    &:disabled {
      background-color: ${theme.palette.grey[5]};
      color: ${theme.palette.grey[60]};
    }
    & svg path {
      fill: ${theme.palette.common.white};
    }
    &:disabled svg path {
      fill: ${theme.palette.grey[60]};
    }
  `,
  [BUTTON_VARIANTS.DESTRUCTIVE_SECONDARY]: (theme) => css`
    ${buttonBaseCss(theme)}
    ${buttonPaddingCss(theme.spacing(0, 6))}
    background-color: ${theme.palette.common.white};
    border: 1px solid ${theme.palette.alert.main};
    color: ${theme.palette.alert.main};
    &:hover,
    &:focus {
      background-color: ${theme.palette.alert.dialog};
    }
    &:disabled {
      border-color: ${theme.palette.grey[40]};
      color: ${theme.palette.grey[60]};
    }
    & svg path {
      fill: ${theme.palette.alert.main};
    }
    &:disabled svg path {
      fill: ${theme.palette.grey[60]};
    }
  `,
  [BUTTON_VARIANTS.TEXT_PRIMARY]: (theme) => css`
    ${buttonBaseCss(theme)}
    ${buttonPaddingCss('0px;')}
    background-color: transparent;
    border-color: transparent;
    color: ${theme.palette.primary[50]};
    &:hover {
      text-decoration: underline;
    }
    &:disabled {
      color: ${theme.palette.grey[60]};
    }
    & svg path {
      fill: ${theme.palette.primary.main};
    }
    &:disabled svg path {
      fill: ${theme.palette.grey[60]};
    }
  `,
  [BUTTON_VARIANTS.TEXT_SECONDARY]: (theme) => css`
  ${buttonBaseCss(theme)}
  ${buttonPaddingCss('0px;')}
  background-color: transparent;
  border-color: transparent;
  color: ${theme.palette.grey[100]};
  &:hover {
    text-decoration: underline;
  }
  &:disabled {
    color: ${theme.palette.grey[60]};
  }
  & svg path {
    fill: ${theme.palette.grey[100]};
  }
  &:disabled svg path {
    fill: ${theme.palette.grey[60]};
  }
`,
  [BUTTON_VARIANTS.TEXT_DESTRUCTIVE]: (theme) => css`
    ${buttonBaseCss(theme)}
    ${buttonPaddingCss('0px;')}
    background-color: transparent;
    border-color: transparent;
    color: ${theme.palette.alert.main};
    &:hover {
      text-decoration: underline;
    }
    &:disabled {
      color: ${theme.palette.grey[60]};
    }
    & svg path {
      fill: ${theme.palette.alert.main};
    }
    &:disabled svg path {
      fill: ${theme.palette.grey[60]};
    }
  `,
};
